import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { StudentsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { useToast } from "@/components/feedback";

export const useStudentBulkDelete = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async (studentIds: number[]) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(StudentsApi, accessToken);
      await Promise.all(
        studentIds.map((id) => service.apiStudentsIdDelete({ id }))
      );
    },
    onSuccess: async (_data, studentIds) => {
      for (const studentId of studentIds) {
        queryClient.removeQueries({ queryKey: [QueryKeys.Students, studentId] });
        queryClient.removeQueries({
          queryKey: [QueryKeys.ClassesEnrollmentsByStudentId, studentId],
        });
        queryClient.removeQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === QueryKeys.StudentAvailableClasses &&
            query.queryKey[1] === studentId,
        });
      }

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UserStudentsGet],
        refetchType: "all",
      });
      await queryClient.invalidateQueries({
        predicate: (query) => {
          if (!Array.isArray(query.queryKey)) return false;
          if (query.queryKey[0] !== QueryKeys.Students) return false;
          const mode = query.queryKey[1];
          return mode === "scroll" || mode === "search";
        },
        refetchType: "all",
      });
      const count = studentIds.length;
      toast({
        title: "Students deleted",
        description: `${count} student${count === 1 ? "" : "s"} deleted successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: any) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to delete students"
      );
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};
